// Google Maps satellite view + drawing tools for the Quick Quote card. Renders a locked-to-
// satellite map centered on the quoted address, with polygon/polyline drawing tools the user can
// trace a roof or a ridge/eave length with. Shape state (the traced list + running totals) is
// OWNED by QuickQuoteCard — this component only creates/destroys the actual Maps overlay
// instances and reports the computed measurements up via `onShapesChange`. The parent removes a
// mis-drawn shape through the imperative `removeShape` handle (it has no other way to reach the
// underlying google.maps.Polygon/Polyline instance).
//
// NOTE ON THE DRAWING TOOLKIT: this does NOT use `google.maps.drawing.DrawingManager`. That legacy
// toolkit was removed from the Maps JavaScript API (@types/google.maps now ships it as a literal
// empty class — "no longer available... as of version 3.65"). Polygon/Polyline tracing is instead
// built directly on the still fully-supported `Map` click events + `Polygon`/`Polyline` overlays +
// `geometry.spherical` area/length helpers: click "Draw Area"/"Draw Length", click the map to add
// vertices (a live preview overlay grows with each click), then "Finish" (or "Cancel").
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { loadGeometryLibrary, loadGoogleMaps } from '@/lib/googleMapsLoader';

const M2_TO_SQFT = 10.7639;
const M_TO_FT = 3.28084;
const SQFT_PER_SQUARE = 100;

const POLYGON_STYLE = { fillColor: '#f97316', fillOpacity: 0.25, strokeColor: '#f97316', strokeWeight: 2 };
const POLYLINE_STYLE = { strokeColor: '#3b82f6', strokeWeight: 3 };

// One-time global style for the on-map edge-length pills (see `createLabelOverlay` below) — a
// plain <style> tag, not a Tailwind class, because these render inside Google's own map panes,
// outside React's tree. Guarded so mounting multiple QuickQuoteMap instances only injects it once.
const LABEL_STYLE_ID = 'qq-edge-label-style';
if (typeof document !== 'undefined' && !document.getElementById(LABEL_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = LABEL_STYLE_ID;
  style.textContent = `
    .qq-edge-label {
      position: absolute;
      transform: translate(-50%, -130%);
      background: rgba(255, 255, 255, 0.92);
      color: #1f2937;
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      padding: 2px 6px;
      border-radius: 4px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      white-space: nowrap;
      pointer-events: none;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    }
  `;
  document.head.appendChild(style);
}

/** A small text pill pinned to a LatLng, positioned above that point via CSS transform (see
 *  `.qq-edge-label` above) — used to show each traced edge/segment's length directly on the map,
 *  not just in the list below it. Built on `OverlayView` (core Maps API, no extra library needed
 *  and no Map ID requirement, unlike AdvancedMarkerElement) rather than `Marker.label`, which has
 *  no supported way to offset the label above its anchor point or give it a background pill. */
function createLabelOverlay(
  mapsApi: typeof google.maps,
  position: google.maps.LatLng,
  text: string,
): google.maps.OverlayView {
  class LabelOverlay extends mapsApi.OverlayView {
    private div: HTMLDivElement | null = null;
    onAdd() {
      const div = document.createElement('div');
      div.className = 'qq-edge-label';
      div.textContent = text;
      this.div = div;
      this.getPanes()?.floatPane.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const point = this.getProjection()?.fromLatLngToDivPixel(position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }
    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  }
  return new LabelOverlay();
}

export interface DrawnShape {
  id: number;
  type: 'polygon' | 'polyline';
  area_m2?: number;
  area_sqft?: number;
  squares?: number;
  length_m?: number;
  length_ft?: number;
  /** Polygon only — the length (ft) of each edge in trace order, including the closing edge back
   *  to the first vertex (e.g. eave/ridge/rake segments a roofer actually needs, not just total
   *  area). Absent for polylines, which already report their own single length_ft. */
  edge_lengths_ft?: number[];
  label: string;
}

export interface QuickQuoteMapHandle {
  /** Remove a previously traced shape (both from the map and the reported shapes list). */
  removeShape: (id: number) => void;
}

interface QuickQuoteMapProps {
  /** Optional: when the property has no stored coordinates the map geocodes `address` instead. */
  lat?: number | null;
  lng?: number | null;
  address: string;
  onShapesChange: (shapes: DrawnShape[]) => void;
}

type DrawMode = 'polygon' | 'polyline' | null;
type Overlay = google.maps.Polygon | google.maps.Polyline;

export const QuickQuoteMap = forwardRef<QuickQuoteMapHandle, QuickQuoteMapProps>(
  ({ lat, lng, address, onShapesChange }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [drawMode, setDrawMode] = useState<DrawMode>(null);
    const [pointCount, setPointCount] = useState(0);

    // Mutable engine state — kept in refs (not React state) since it drives imperative Maps SDK
    // calls, not renders; `onShapesChange` is how the traced list reaches React (QuickQuoteCard).
    const shapesRef = useRef<DrawnShape[]>([]);
    const overlaysRef = useRef<Map<number, Overlay>>(new Map());
    // Per-shape on-map length labels (one per edge/segment) — tracked separately from `overlaysRef`
    // since a shape owns several of these, not one.
    const labelOverlaysRef = useRef<Map<number, google.maps.OverlayView[]>>(new Map());
    const nextIdRef = useRef(1);
    const onShapesChangeRef = useRef(onShapesChange);
    onShapesChangeRef.current = onShapesChange;
    const mapsRef = useRef<typeof google.maps | null>(null);
    const geometryRef = useRef<google.maps.GeometryLibrary | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const dblclickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);

    // In-progress trace: the vertices placed so far + the live preview overlay showing them.
    const drawModeRef = useRef<DrawMode>(null);
    const activePathRef = useRef<google.maps.LatLng[]>([]);
    const previewOverlayRef = useRef<Overlay | null>(null);

    const resetDraw = () => {
      previewOverlayRef.current?.setMap(null);
      previewOverlayRef.current = null;
      activePathRef.current = [];
      drawModeRef.current = null;
      setDrawMode(null);
      setPointCount(0);
      mapRef.current?.setOptions({ draggableCursor: '', draggingCursor: '' });
    };

    const startDraw = (type: 'polygon' | 'polyline') => {
      const maps = mapsRef.current;
      const map = mapRef.current;
      if (!maps || !map) return;
      previewOverlayRef.current?.setMap(null);
      activePathRef.current = [];
      drawModeRef.current = type;
      setDrawMode(type);
      setPointCount(0);
      map.setOptions({ draggableCursor: 'crosshair', draggingCursor: 'crosshair', disableDoubleClickZoom: true });
      previewOverlayRef.current = type === 'polygon'
        ? new maps.Polygon({ map, paths: [], clickable: false, editable: false, zIndex: 2, ...POLYGON_STYLE })
        : new maps.Polyline({ map, path: [], clickable: false, editable: false, zIndex: 2, ...POLYLINE_STYLE });
    };

    const finishDraw = () => {
      const type = drawModeRef.current;
      const maps = mapsRef.current;
      const map = mapRef.current;
      const geometry = geometryRef.current;
      const overlay = previewOverlayRef.current;
      const path = activePathRef.current;
      const minPoints = type === 'polygon' ? 3 : 2;
      if (!type || !maps || !map || !geometry || !overlay || path.length < minPoints) return;

      const id = nextIdRef.current++;
      overlaysRef.current.set(id, overlay);
      const labels: google.maps.OverlayView[] = [];
      let shape: DrawnShape;
      if (type === 'polygon') {
        const area_m2 = geometry.spherical.computeArea(path);
        const area_sqft = area_m2 * M2_TO_SQFT;
        const squares = area_sqft / SQFT_PER_SQUARE;
        // Each side's length, in trace order, including the closing edge (last vertex -> first) —
        // computeArea treats the path as already closed, so the edge list should match. A label
        // pill goes up at each edge's geodesic midpoint, right on the map, not just in the list.
        const edge_lengths_ft = path.map((point, i) => {
          const next = path[(i + 1) % path.length];
          const lenFt = geometry.spherical.computeDistanceBetween(point, next) * M_TO_FT;
          const mid = geometry.spherical.interpolate(point, next, 0.5);
          const lbl = createLabelOverlay(maps, mid, `${Math.round(lenFt)} ft`);
          lbl.setMap(map);
          labels.push(lbl);
          return lenFt;
        });
        shape = {
          id, type: 'polygon', area_m2, area_sqft, squares, edge_lengths_ft,
          label: `Area ${shapesRef.current.filter((s) => s.type === 'polygon').length + 1}`,
        };
      } else {
        const length_m = geometry.spherical.computeLength(path);
        const length_ft = length_m * M_TO_FT;
        // Label each individual segment (not just the total) — same reasoning as the polygon edges.
        for (let i = 0; i < path.length - 1; i++) {
          const segFt = geometry.spherical.computeDistanceBetween(path[i], path[i + 1]) * M_TO_FT;
          const mid = geometry.spherical.interpolate(path[i], path[i + 1], 0.5);
          const lbl = createLabelOverlay(maps, mid, `${Math.round(segFt)} ft`);
          lbl.setMap(map);
          labels.push(lbl);
        }
        shape = {
          id, type: 'polyline', length_m, length_ft,
          label: `Length ${shapesRef.current.filter((s) => s.type === 'polyline').length + 1}`,
        };
      }
      labelOverlaysRef.current.set(id, labels);
      shapesRef.current = [...shapesRef.current, shape];
      onShapesChangeRef.current(shapesRef.current);

      // The overlay just placed becomes the finalized shape — don't tear it down.
      previewOverlayRef.current = null;
      activePathRef.current = [];
      drawModeRef.current = null;
      setDrawMode(null);
      setPointCount(0);
      map.setOptions({ draggableCursor: '', draggingCursor: '', disableDoubleClickZoom: false });
      // Finish closes the full-screen view too, so the user lands back on the card where the
      // traced area has just been written into the quote.
      if (document.fullscreenElement) void document.exitFullscreen?.();
    };

    useImperativeHandle(ref, () => ({
      removeShape(id: number) {
        const overlay = overlaysRef.current.get(id);
        if (overlay) {
          overlay.setMap(null);
          overlaysRef.current.delete(id);
        }
        const labels = labelOverlaysRef.current.get(id);
        if (labels) {
          labels.forEach((l) => l.setMap(null));
          labelOverlaysRef.current.delete(id);
        }
        shapesRef.current = shapesRef.current.filter((s) => s.id !== id);
        onShapesChangeRef.current(shapesRef.current);
      },
    }), []);

    useEffect(() => {
      let cancelled = false;
      // Captured once here (not read from the ref inside the cleanup closure below) — this Map
      // instance is created exactly once by useRef and only ever mutated via .set()/.delete(), so
      // this is the same object cleanup would see either way, just without tripping the
      // exhaustive-deps "ref value may have changed by cleanup time" lint rule.
      const overlays = overlaysRef.current;
      const labelOverlays = labelOverlaysRef.current;

      (async () => {
        try {
          const [maps, geometry] = await Promise.all([loadGoogleMaps(), loadGeometryLibrary()]);
          if (cancelled || !containerRef.current) return;
          mapsRef.current = maps;
          geometryRef.current = geometry;

          let center: google.maps.LatLngLiteral | null = lat != null && lng != null ? { lat, lng } : null;
          if (!center) {
            const found = await new maps.Geocoder().geocode({ address });
            const loc = found.results[0]?.geometry.location;
            if (!loc) throw new Error('address not found');
            center = { lat: loc.lat(), lng: loc.lng() };
          }
          if (cancelled || !containerRef.current) return;
          const here = center;

          const map = new maps.Map(containerRef.current, {
            center: here,
            zoom: 20,
            mapTypeId: 'satellite',
            disableDefaultUI: false,
            mapTypeControl: false,
            streetViewControl: false,
            // Off on purpose: Google's own control fullscreens only the map element, which hides
            // this card's Draw/Finish toolbar. The wrapper's own toggle below keeps it on screen.
            fullscreenControl: false,
          });
          mapRef.current = map;

          markerRef.current = new maps.Marker({ position: here, map, title: address });

          // Google Maps zooms toward the cursor/pinch point by default, not the map center — a
          // scroll-wheel or pinch zoom can drift the property marker off to one side or out of
          // view entirely. Snap back to center on the quoted address once zoom settles, so the
          // property stays the visual anchor no matter how the user zoomed.
          //
          // Calling setCenter directly from 'zoom_changed' does NOT work reliably: that event
          // fires as soon as the zoom level itself changes, which is BEFORE Maps' own internal
          // zoom-to-cursor pan has finished — the pan that follows overrides an immediate
          // setCenter, so the marker still drifts. Flag on 'zoom_changed' and act on the next
          // 'idle' instead, which fires only once ALL pending pan/zoom/tile-load work is done.
          let recenterPending = false;
          zoomListenerRef.current = map.addListener('zoom_changed', () => {
            recenterPending = true;
          });
          idleListenerRef.current = map.addListener('idle', () => {
            if (!recenterPending) return;
            recenterPending = false;
            map.setCenter(here);
          });

          clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!drawModeRef.current || !e.latLng) return;
            activePathRef.current = [...activePathRef.current, e.latLng];
            const overlay = previewOverlayRef.current;
            if (overlay instanceof maps.Polygon) overlay.setPath(activePathRef.current);
            else if (overlay instanceof maps.Polyline) overlay.setPath(activePathRef.current);
            setPointCount(activePathRef.current.length);
          });

          // Double-click finishes the current drawing. The browser fires two 'click' events
          // before 'dblclick', so we pop the last vertex (added by the second click) before
          // finalising to avoid a duplicate point on the shape.
          dblclickListenerRef.current = map.addListener('dblclick', () => {
            if (!drawModeRef.current) return;
            if (activePathRef.current.length > 0) {
              activePathRef.current = activePathRef.current.slice(0, -1);
              const overlay = previewOverlayRef.current;
              if (overlay instanceof maps.Polygon) overlay.setPath(activePathRef.current);
              else if (overlay instanceof maps.Polyline) overlay.setPath(activePathRef.current);
              setPointCount(activePathRef.current.length);
            }
            finishDraw();
          });

          setLoading(false);
        } catch {
          if (!cancelled) {
            setError(lat != null && lng != null
              ? 'Could not load Google Maps. Check your connection or the map configuration.'
              : "Couldn't find this address on the map.");
            setLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
        clickListenerRef.current?.remove();
        clickListenerRef.current = null;
        dblclickListenerRef.current?.remove();
        dblclickListenerRef.current = null;
        zoomListenerRef.current?.remove();
        zoomListenerRef.current = null;
        idleListenerRef.current?.remove();
        idleListenerRef.current = null;
        previewOverlayRef.current?.setMap(null);
        previewOverlayRef.current = null;
        overlays.forEach((overlay) => overlay.setMap(null));
        overlays.clear();
        labelOverlays.forEach((labels) => labels.forEach((l) => l.setMap(null)));
        labelOverlays.clear();
        shapesRef.current = [];
        markerRef.current?.setMap(null);
        markerRef.current = null;
        mapRef.current = null;
      };
      // Intentionally re-run only when the quoted location itself changes — lat/lng/address come
      // from a fresh SAM action (a new quick quote), which is exactly when the map should rebuild.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng, address]);

    if (error) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-ui-subtle px-4 text-center text-xs text-ui-text3">
          {error}
        </div>
      );
    }

    const minPoints = drawMode === 'polygon' ? 3 : 2;
    const canFinish = drawMode !== null && pointCount >= minPoints;

    const toggleFullscreen = () => {
      const root = rootRef.current;
      if (!root) return;
      if (document.fullscreenElement) void document.exitFullscreen?.();
      else void root.requestFullscreen?.();
    };

    return (
      <div ref={rootRef} data-qq-map-root className="relative h-full w-full bg-ui">
        <div ref={containerRef} className="h-full w-full" />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-ui-subtle text-xs text-ui-text3">
            Loading map…
          </div>
        )}
        {!loading && (
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label="Toggle full screen"
            className="absolute right-2 top-2 z-10 rounded-md border border-ui-border bg-ui-card px-2 py-1 text-xs font-medium text-ui-text shadow-md hover:bg-ui-hover"
          >
            ⛶
          </button>
        )}
        {!loading && (
          <div className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-lg border border-ui-border bg-ui-card px-1.5 py-1 shadow-md">
            {drawMode === null ? (
              <>
                <button
                  onClick={() => startDraw('polygon')}
                  className="rounded-md px-2 py-1 text-xs font-medium text-ui-text hover:bg-ui-hover"
                >
                  ▱ Draw Area
                </button>
                <button
                  onClick={() => startDraw('polyline')}
                  className="rounded-md px-2 py-1 text-xs font-medium text-ui-text hover:bg-ui-hover"
                >
                  ╱ Draw Length
                </button>
              </>
            ) : (
              <>
                <span className="px-1.5 text-xs text-ui-text2">
                  {drawMode === 'polygon' ? 'Click corners · double-click to finish' : 'Click points · double-click to finish'}
                  {pointCount > 0 && ` (${pointCount})`}
                </span>
                <button
                  onClick={finishDraw}
                  disabled={!canFinish}
                  className="rounded-md btn-sheen px-2 py-1 text-xs font-medium text-on-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Finish
                </button>
                <button
                  onClick={resetDraw}
                  aria-label="Cancel drawing"
                  className="rounded-md px-2 py-1 text-xs font-medium text-ui-text3 hover:bg-ui-hover hover:text-ui-text"
                >
                  ×
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

QuickQuoteMap.displayName = 'QuickQuoteMap';

export default QuickQuoteMap;
